import { useEffect, useState, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '@/services/supabaseClient'
import EmsLogo from '@/components/EmsLogo'



interface Exam {
  id: number
  exam_name: string
  year: number
  is_live: boolean
  teacher_entry_enabled: boolean
  student_result_live: boolean
  class_6: number
  class_7: number
  class_8: number
  class_9: number
  class_10: number
  class_11: number
  class_12: number
}

interface SubjectComp { CQ?: string; MCQ?: string; Practical?: string; Total?: string; GPA?: string }
interface SubjectRule {
  id: number
  subject_id?: number
  subject_name: string
  subject_code: string
  pass_cq: number
  pass_mcq: number
  pass_practical: number
  pass_total: number
  full_marks: number
  total_cq: number
  total_mcq: number
  total_practical: number
  exam_class?: any[]
}

interface StudentRow { [key: string]: unknown }



export default function ExamPanelPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [exam, setExam] = useState<Exam | null>(null)
  const [loading, setLoading] = useState(true)
  const [status, setStatus] = useState('')
  const [activeTab, setActiveTab] = useState<'overview' | 'setup' | 'marks' | 'reports' | 'optional'>('overview')
  const [isEditingCounts, setIsEditingCounts] = useState(false)
  const [draftCounts, setDraftCounts] = useState<Record<number, number>>({})
  
  const [showDetails, setShowDetails] = useState(false)
  const [data, setData] = useState<StudentRow[]>([])
  const [subjectMap, setSubjectMap] = useState<Record<string, SubjectComp>>({})
  const [fixedCols] = useState<string[]>(['roll'])
  const editChanges = useRef<Record<string, any>>({})

  // Unlock Final Submit state
  const [showUnlockModal, setShowUnlockModal] = useState(false)
  const [submittedAssignments, setSubmittedAssignments] = useState<any[]>([])
  const [loadingSubmitted, setLoadingSubmitted] = useState(false)

  // Manual & CSV upload state
  const [showManualModal, setShowManualModal] = useState(false)
  const [showCsvModal, setShowCsvModal] = useState(false)
  const [manualForm, setManualForm] = useState({
    studentNameEn: '',
    fatherNameEn: '',
    studentClass: '',
    studentSection: '',
    studentRoll: '',
    optionalSubject: '',
    fatherMobile: ''
  })
  const [csvStudents, setCsvStudents] = useState<any[]>([])
  const [isProcessingCsv, setIsProcessingCsv] = useState(false)

  // Filters / Import state
  const [gridClass, setGridClass] = useState('')
  const [gridSection, setGridSection] = useState('')
  const [availableClasses, setAvailableClasses] = useState<string[]>([])
  const [classSectionsMap, setClassSectionsMap] = useState<Record<string, string[]>>({})
  const [filterSubject, setFilterSubject] = useState('')
  const [sourceYear, setSourceYear] = useState<string>('')
  const [availableYears, setAvailableYears] = useState<string[]>([])
  const [sourceClasses, setSourceClasses] = useState<string[]>([])
  const [sourceSectionsByClass, setSourceSectionsByClass] = useState<Record<string, string[]>>({})
  const [previewCount, setPreviewCount] = useState<number | null>(null)
  const [subjectRules, setSubjectRules] = useState<SubjectRule[]>([])
  const [editingRule, setEditingRule] = useState<SubjectRule | null>(null)
  
  // Report Summary state
  const [summaryData, setSummaryData] = useState({
    passCount: 0,
    failCount: 0,
    topStudents: [] as StudentRow[]
  })

  useEffect(() => {
    if (sourceYear) {
      loadSourceMetadata()
    } else {
      setSourceClasses([])
      setSourceSectionsByClass({})
    }
  }, [sourceYear])

  async function loadSourceMetadata() {
    if (!sourceYear) return
    const classCol = `class_${sourceYear}`
    const sectionCol = `section_${sourceYear}`
    
    setStatus(`🔍 Scanning database for Class/Section metadata (${sourceYear})...`)
    
    let allStudents: any[] = []
    let from = 0
    let to = 999
    let hasMore = true

    while (hasMore) {
      const { data, error } = await supabase
        .from('student_database')
        .select(`${classCol}, ${sectionCol}`)
        .not(classCol, 'is', null)
        .range(from, to)
      
      if (error) { 
        setStatus('Error loading metadata: ' + error.message)
        hasMore = false
        return 
      }
      
      if (data && data.length > 0) {
        allStudents = [...allStudents, ...data]
        if (data.length < 1000) {
          hasMore = false
        } else {
          from += 1000
          to += 1000
        }
      } else {
        hasMore = false
      }
    }

    const classes = new Set<string>()
    const classToSections: Record<string, Set<string>> = {}

    allStudents.forEach(s => {
      const c = String(s[classCol] ?? '').trim()
      const sec = String(s[sectionCol] ?? '').trim()
      if (c && c !== 'null') {
        classes.add(c)
        if (!classToSections[c]) classToSections[c] = new Set()
        if (sec && sec !== 'null') classToSections[c].add(sec)
      }
    })

    const sortedClasses = Array.from(classes).sort((a,b) => {
      const na = parseInt(a), nb = parseInt(b)
      if (!isNaN(na) && !isNaN(nb)) return na - nb
      return a.localeCompare(b)
    })

    setSourceClasses(sortedClasses)
    const finalSections: Record<string, string[]> = {}
    Object.keys(classToSections).forEach(c => {
      finalSections[c] = Array.from(classToSections[c]).sort()
    })
    setSourceSectionsByClass(finalSections)
    setStatus(`✅ Metadata loaded. Found ${sortedClasses.length} classes.`)
  }

  const [totalEnrolled, setTotalEnrolled] = useState<number | null>(null)

  useEffect(() => {
    if (!id) return
    loadExamData()
    loadMetadata()
    loadReportSummary()
    loadTotalEnrollment()
    // Set a stable range of years
    setAvailableYears(['2024', '2025', '2026', '2027', '2028', '2029', '2030'])
  }, [id])

  async function loadTotalEnrollment() {
    const { count, error } = await supabase.from('FMHS_exam_data').select('*', { count: 'exact', head: true }).eq('exam_id', id)
    if (!error) setTotalEnrolled(count)
  }

  async function loadReportSummary() {
    let rows: any[] = []
    let from = 0
    let to = 999
    let hasMore = true
    while (hasMore) {
      const { data, error } = await supabase.from('FMHS_exam_data').select('*').eq('exam_id', id).range(from, to)
      if (error) break
      if (data && data.length > 0) {
        rows = [...rows, ...data]
        if (data.length < 1000) hasMore = false
        else { from += 1000; to += 1000 }
      } else { hasMore = false }
    }

    if (rows.length > 0) {
      const pass = rows.filter(r => r.status === 'Pass' || r.gpa_final !== 'F' && Number(r.gpa_final) > 0).length
      const fail = rows.filter(r => r.status === 'Fail' || r.gpa_final === 'F').length
      const top = [...rows].sort((a,b) => (Number(b.total_mark)||0) - (Number(a.total_mark)||0)).slice(0, 3)
      setSummaryData({ passCount: pass, failCount: fail, topStudents: top })
    }
  }

  useEffect(() => {
    if (gridClass && sourceYear) {
      updatePreviewCount()
    }
  }, [gridClass, gridSection, sourceYear])

  async function updatePreviewCount() {
    if (!gridClass || !sourceYear) { setPreviewCount(null); return }
    const classCol = `class_${sourceYear}`
    const sectionCol = `section_${sourceYear}`
    
    // 1. Get all candidate students from database
    // Exclude: status=TC AND student_final_status=false
    let students: any[] = []
    let from = 0
    let to = 999
    let hasMore = true
    while (hasMore) {
      let q = supabase.from('student_database').select('iid, student_final_status').eq(classCol, gridClass)
      if (gridSection && gridSection !== 'All') q = q.eq(sectionCol, gridSection)
      const { data, error } = await q.range(from, to)
      if (error) break
      if (data && data.length > 0) {
        // Filter out: student_final_status = false
        const filtered = data.filter((s: any) => s.student_final_status !== false)
        students = [...students, ...filtered]
        if (data.length < 1000) hasMore = false
        else { from += 1000; to += 1000 }
      } else { hasMore = false }
    }
    if (students.length === 0) { setPreviewCount(0); return }

    // 2. Get students already in this specific exam
    let existing: any[] = []
    from = 0; to = 999; hasMore = true
    while (hasMore) {
      const { data, error } = await supabase.from('FMHS_exam_data').select('iid').eq('exam_id', id).range(from, to)
      if (error) break
      if (data && data.length > 0) {
        existing = [...existing, ...data]
        if (data.length < 1000) hasMore = false
        else { from += 1000; to += 1000 }
      } else { hasMore = false }
    }
    
    const existingIds = new Set(existing.map(e => String(e.iid)))
    const netCount = students.filter(s => !existingIds.has(String(s.iid))).length
    
    setPreviewCount(netCount)
  }

  useEffect(() => {
    const map: Record<string, SubjectComp> = {}
    subjectRules.forEach(r => {
      const base = `*${r.subject_name}`
      map[r.subject_name] = {
        CQ: r.total_cq > 0 ? `${base}_CQ` : undefined,
        MCQ: r.total_mcq > 0 ? `${base}_MCQ` : undefined,
        Practical: r.total_practical > 0 ? `${base}_Practical` : undefined,
        Total: `${base}_Total`,
        GPA: `${base}_GPA`
      }
    })
    setSubjectMap(map)
  }, [subjectRules])

  async function loadExamData() {
    const { data: ex } = await supabase.from('FMHS_exams_names').select('*').eq('id', id).single()
    if (ex) {
      setExam(ex)
      const counts: Record<number, number> = {}
      for (let i = 6; i <= 12; i++) counts[i] = (ex as any)[`class_${i}`] || 0
      setDraftCounts(counts)
    }
    
    const { data: rules } = await supabase.from('FMHS_exam_subjects').select('*').eq('exam_id', id)
    setSubjectRules(rules || [])
    setLoading(false)
  }

  async function loadMetadata() {
    let rows: any[] = []
    let from = 0; let to = 999; let hasMore = true
    while (hasMore) {
      const { data, error } = await supabase.from('FMHS_exam_data').select('class, section').eq('exam_id', Number(id)).range(from, to)
      if (error) break
      if (data && data.length > 0) {
        rows = [...rows, ...data]
        if (data.length < 1000) hasMore = false
        else { from += 1000; to += 1000 }
      } else { hasMore = false }
    }

    if (rows.length > 0) {
      setAvailableClasses(Array.from(new Set(rows.map(r => String(r.class)))).sort((a,b) => Number(a)-Number(b)))

      // Build class-to-sections mapping dynamically
      const mapping: Record<string, Set<string>> = {}
      rows.forEach(r => {
        const c = String(r.class)
        const s = String(r.section)
        if (c) {
          if (!mapping[c]) mapping[c] = new Set()
          if (s) mapping[c].add(s)
        }
      })
      const finalMapping: Record<string, string[]> = {}
      Object.keys(mapping).forEach(c => {
        finalMapping[c] = Array.from(mapping[c]).sort()
      })
      setClassSectionsMap(finalMapping)
    }
  }

  async function loadMarks() {
    await loadMarksExplicitly(gridClass, gridSection, filterSubject)
  }

  async function loadMarksExplicitly(cVal: string, sVal: string, subVal: string) {
    if (!cVal) return
    setLoading(true)
    
    // RESET STATES TO PREVENT DATA LEAKAGE
    editChanges.current = {}
    setSavingRows({})

    let rows: any[] = []
    let from = 0; let to = 999; let hasMore = true
    while (hasMore) {
      let q = supabase.from('FMHS_exam_data').select('*').eq('exam_id', Number(id)).eq('class', Number(cVal))
      if (sVal && sVal !== 'All') q = q.eq('section', sVal)
      const { data, error } = await q.order('roll', { ascending: true }).range(from, to)
      if (error) break
      if (data && data.length > 0) {
        rows = [...rows, ...data]
        if (data.length < 1000) hasMore = false
        else { from += 1000; to += 1000 }
      } else { hasMore = false }
    }

    // RESTORE FROM LOCAL STORAGE
    const localKey = `admin_unsaved_${id}_${cVal}_${sVal}_${subVal}`
    const localData = localStorage.getItem(localKey)
    if (localData) {
      try {
        const parsed = JSON.parse(localData)
        editChanges.current = parsed
        
        // MERGE INTO ROWS FOR UI
        rows.forEach(r => {
          if (parsed[r.id]) {
            Object.assign(r, parsed[r.id])
            const subject = subjectMap[subVal]
            const totalCol = subject?.Total
            const componentCols = [subject?.CQ, subject?.MCQ, subject?.Practical].filter(Boolean) as string[]
            const hasComponentDraft = componentCols.some(col => Object.prototype.hasOwnProperty.call(parsed[r.id], col))
            if (totalCol && hasComponentDraft) {
              const totalValue = calculateDraftTotal(String(r.id), r as StudentRow, subVal)
              r[totalCol] = totalValue
            }
          }
        })

        setSavingRows(Object.keys(parsed).reduce((acc, rid) => ({ ...acc, [rid]: 'pending' }), {}))
      } catch (e) { console.error('Local restore error', e) }
    }

    if (rows.length > 0) {
      setData(rows)
    } else {
      setData([])
    }
    setLoading(false)
  }

  const [savingRows, setSavingRows] = useState<Record<string, 'pending' | 'saving' | 'success'>>({})

  function getRowById(rowId: string) {
    return data.find(row => String(row.id) === rowId)
  }

  function calculateDraftTotal(rowId: string, row: StudentRow, subjectKey: string) {
    const subject = subjectKey ? subjectMap[subjectKey] : undefined
    const totalCol = subject?.Total
    if (!totalCol) return null

    const componentCols = [subject.CQ, subject.MCQ, subject.Practical].filter(Boolean) as string[]
    if (componentCols.length === 0) return null

    const draft = editChanges.current[rowId] ?? {}
    const hasComponentDraft = componentCols.some(col => Object.prototype.hasOwnProperty.call(draft, col))
    if (!hasComponentDraft) return null

    let sum = 0
    let hasValue = false
    componentCols.forEach(col => {
      const raw = Object.prototype.hasOwnProperty.call(draft, col) ? draft[col] : row[col]
      if (raw !== null && raw !== undefined && raw !== '') {
        const numeric = Number(raw)
        if (Number.isFinite(numeric)) {
          sum += numeric
          hasValue = true
        }
      }
    })

    return hasValue ? sum : null
  }

  function syncDraftTotal(rowId: string, row: StudentRow, subjectKey: string) {
    const subject = subjectKey ? subjectMap[subjectKey] : undefined
    const totalCol = subject?.Total
    if (!totalCol) return null

    const totalValue = calculateDraftTotal(rowId, row, subjectKey)
    if (!editChanges.current[rowId]) editChanges.current[rowId] = {}

    if (totalValue === null) delete editChanges.current[rowId][totalCol]
    else editChanges.current[rowId][totalCol] = totalValue

    if (Object.keys(editChanges.current[rowId]).length === 0) {
      delete editChanges.current[rowId]
    }

    return totalValue
  }

  function handleEdit(rowId: string, col: string, value: string) {
    const row = getRowById(rowId)
    if (!row) return

    const parsedValue = value === '' ? null : Number(value)
    if (value !== '' && !Number.isFinite(parsedValue)) return

    if (!editChanges.current[rowId]) editChanges.current[rowId] = {}

    if (parsedValue === row[col] || (parsedValue === null && (row[col] === null || row[col] === undefined || row[col] === ''))) {
      delete editChanges.current[rowId][col]
    } else {
      editChanges.current[rowId][col] = parsedValue
    }

    const totalValue = syncDraftTotal(rowId, row, filterSubject)
    
    const totalCol = filterSubject ? subjectMap[filterSubject]?.Total : undefined
    const nextRow: StudentRow = { ...row, [col]: parsedValue }
    if (totalCol && totalValue !== null) {
      nextRow[totalCol] = totalValue
    }

    setData(prev => prev.map(r => String(r.id) === rowId ? nextRow : r))

    // Set status to pending (orange)
    setSavingRows(prev => ({ ...prev, [rowId]: 'pending' }))

    // SAVE TO LOCAL STORAGE
    const localKey = `admin_unsaved_${id}_${gridClass}_${gridSection}_${filterSubject}`
    localStorage.setItem(localKey, JSON.stringify(editChanges.current))
  }

  async function saveRow(rowId: string) {
    const row = getRowById(rowId)
    if (!row) return
    syncDraftTotal(rowId, row, filterSubject)
    if (!editChanges.current[rowId]) return
    setSavingRows(prev => ({ ...prev, [rowId]: 'saving' }))
    
    const { error } = await supabase.from('FMHS_exam_data').update(editChanges.current[rowId]).eq('id', rowId)
    
    if (!error) {
      setSavingRows(prev => ({ ...prev, [rowId]: 'success' }))
      
      // CLEAR FROM LOCAL STORAGE
      delete editChanges.current[rowId]
      const localKey = `admin_unsaved_${id}_${gridClass}_${gridSection}_${filterSubject}`
      if (Object.keys(editChanges.current).length === 0) localStorage.removeItem(localKey)
      else localStorage.setItem(localKey, JSON.stringify(editChanges.current))

      setTimeout(() => {
        setSavingRows(prev => {
          const newStatus = { ...prev }
          if (newStatus[rowId] === 'success') delete newStatus[rowId]
          return newStatus
        })
      }, 3000)
    } else {
      setSavingRows(prev => ({ ...prev, [rowId]: 'pending' })) // revert on error
      alert('Error: ' + error.message)
    }
  }

  async function saveAllChanges() {
    const rowIds = Object.keys(editChanges.current)
    if (rowIds.length === 0) return

    setStatus(`💾 Saving changes for ${rowIds.length} students...`)
    let done = 0
    for (const rid of rowIds) {
      const row = getRowById(rid)
      if (row) syncDraftTotal(rid, row, filterSubject)
      if (!editChanges.current[rid]) {
        setSavingRows(prev => ({ ...prev, [rid]: 'pending' }))
        continue
      }
      setSavingRows(prev => ({ ...prev, [rid]: 'saving' }))
      const { error } = await supabase.from('FMHS_exam_data').update(editChanges.current[rid]).eq('id', rid)
      if (!error) {
        done++
        setSavingRows(prev => ({ ...prev, [rid]: 'success' }))
        delete editChanges.current[rid]
      } else {
        setSavingRows(prev => ({ ...prev, [rid]: 'pending' }))
      }
    }
    
    // CLEAR LOCAL STORAGE
    const localKey = `admin_unsaved_${id}_${gridClass}_${gridSection}_${filterSubject}`
    if (Object.keys(editChanges.current).length === 0) localStorage.removeItem(localKey)
    else localStorage.setItem(localKey, JSON.stringify(editChanges.current))

    setStatus(`✅ Successfully saved ${done} records.`)
    setTimeout(() => {
        setStatus('')
        setSavingRows({})
    }, 5000)
  }

  async function resetLocalChanges() {
    if (!confirm('Are you sure? This will clear all unsaved changes for this selection and reload data from the database.')) return
    const localKey = `admin_unsaved_${id}_${gridClass}_${gridSection}_${filterSubject}`
    localStorage.removeItem(localKey)
    editChanges.current = {}
    setSavingRows({})
    await loadMarksExplicitly(gridClass, gridSection, filterSubject)
    setStatus('🔄 Local changes cleared. Original data reloaded.')
    setTimeout(() => setStatus(''), 3000)
  }


  async function loadSubmittedAssignments() {
    setLoadingSubmitted(true)
    const { data: assignments, error } = await supabase
      .from('FMHS_exam_teacher_selection')
      .select('*')
      .eq('exam_id', Number(id))
      .eq('final_submitted', true)
      .order('class', { ascending: true })
      .order('section', { ascending: true })
    
    if (!error && assignments) {
      setSubmittedAssignments(assignments)
    } else {
      console.error('Error fetching submitted assignments:', error)
    }
    setLoadingSubmitted(false)
  }

  async function handleUnlockAssignment(assignmentId: number, subjectName: string) {
    if (!confirm(`Are you sure you want to unlock "${subjectName}"? This will allow the teacher to edit marks again.`)) return
    
    const { error } = await supabase
      .from('FMHS_exam_teacher_selection')
      .update({ final_submitted: false })
      .eq('id', assignmentId)

    if (error) {
      alert('Failed to unlock: ' + error.message)
    } else {
      setStatus('✅ Subject unlocked successfully!')
      setTimeout(() => setStatus(''), 3000)
      loadSubmittedAssignments()
    }
  }


  async function toggleStatus(field: 'is_live' | 'teacher_entry_enabled' | 'student_result_live', current: boolean) {
    if (!exam) return

    // 1. Optimistic local update for instantaneous response
    const updatedValue = !current
    setExam(prev => prev ? { ...prev, [field]: updatedValue } : null)

    // 2. Database update in background
    const { error } = await supabase.from('FMHS_exams_names').update({ [field]: updatedValue }).eq('id', id)
    
    if (error) {
      alert(error.message)
      // Revert state if the database update failed
      setExam(prev => prev ? { ...prev, [field]: current } : null)
    } else {
      // Quiet background refresh to keep state robust and verified
      const { data: ex } = await supabase.from('FMHS_exams_names').select('*').eq('id', id).single()
      if (ex) {
        setExam(ex)
      }
    }
  }

  async function saveClassCounts() {
    setStatus('💾 Saving configuration...')
    const { error } = await supabase.from('FMHS_exams_names').update({
      class_6: draftCounts[6],
      class_7: draftCounts[7],
      class_8: draftCounts[8],
      class_9: draftCounts[9],
      class_10: draftCounts[10],
      class_11: draftCounts[11],
      class_12: draftCounts[12],
    }).eq('id', id)

    if (error) {
      alert(error.message)
      setStatus('❌ Save failed.')
    } else {
      setStatus('✅ Configuration saved successfully!')
      setIsEditingCounts(false)
      loadExamData()
      setTimeout(() => setStatus(''), 3000)
    }
  }

  async function importStudents() {
    if (!exam || !gridClass || !sourceYear) { alert('Please select class and year.'); return }
    setStatus(`🔍 Scanning database for ${sourceYear} session...`)
    
    const classCol = `class_${sourceYear}`
    const sectionCol = `section_${sourceYear}`
    const rollCol = `roll_${sourceYear}`

    let students: any[] = []
    let from = 0; let to = 999; let hasMore = true
    while (hasMore) {
      let q = supabase.from('student_database')
        .select(`iid, student_name_en, father_name_en, student_final_status, ${classCol}, ${sectionCol}, ${rollCol}`)
        .eq(classCol, gridClass)
      if (gridSection && gridSection !== 'All') q = q.eq(sectionCol, gridSection)
      const { data, error } = await q.range(from, to)
      if (error) { alert(`Error fetching students: ${error.message}`); return }
      if (data && data.length > 0) {
        // Filter out: student_final_status = false
        const filtered = (data as any[]).filter((s: any) => s.student_final_status !== false)
        students = [...students, ...filtered]
        if (data.length < 1000) hasMore = false
        else { from += 1000; to += 1000 }
      } else { hasMore = false }
    }
    if (students.length === 0) { alert(`No active students (non-TC) found for Class ${gridClass} in ${sourceYear}.`); return }

    setStatus(`📦 Importing ${students.length} students...`)
    
    let existing: any[] = []
    from = 0; to = 999; hasMore = true
    while (hasMore) {
      const { data, error } = await supabase.from('FMHS_exam_data').select('iid').eq('exam_id', id).range(from, to)
      if (error) break
      if (data && data.length > 0) {
        existing = [...existing, ...data]
        if (data.length < 1000) hasMore = false
        else { from += 1000; to += 1000 }
      } else { hasMore = false }
    }
    const existingIds = new Set(existing.map(e => String(e.iid)))
    
    const newStudents = (students as any[]).filter((s: any) => !existingIds.has(String(s.iid)))
    if (newStudents.length === 0) {
      alert(`All students already imported.`)
      setStatus('No new students found.')
      return
    }

    const toInsert = (newStudents as any[]).map((s: any) => ({
      exam_id: id,
      iid: s.iid,
      class: Number(s[classCol]),
      section: s[sectionCol],
      roll: Number(s[rollCol]),
      student_name_en: s.student_name_en,
      father_name_en: s.father_name_en,
      exam_name_year: `${exam?.exam_name}-${exam?.year}`,
      status: 'Pending'
    }))

    const { error: insErr } = await supabase.from('FMHS_exam_data').insert(toInsert)
    if (insErr) {
      alert(insErr.message)
    } else {
      setStatus(`✅ Successfully imported ${newStudents.length} students!`)
      loadMarks()
      loadMetadata()
    }
  }

  async function handleAddManualStudent(e: React.FormEvent) {
    e.preventDefault()
    if (!exam) return

    const { studentNameEn, fatherNameEn, studentClass, studentSection, studentRoll, optionalSubject, fatherMobile } = manualForm
    if (!studentNameEn || !fatherNameEn || !studentClass || !studentRoll || !studentSection) {
      alert('Please fill in all required fields.')
      return
    }

    setStatus('Adding student...')
    
    // 1. Insert into student_database
    const sessionYear = exam.year
    const studentDbPayload: Record<string, any> = {
      student_name_en: studentNameEn.trim(),
      father_name_en: fatherNameEn.trim(),
      optional_subject: optionalSubject.trim() || null,
      active_class: Number(studentClass),
      active_section: studentSection.trim(),
      active_roll: Number(studentRoll),
      [`class_${sessionYear}`]: Number(studentClass),
      [`section_${sessionYear}`]: studentSection.trim(),
      [`roll_${sessionYear}`]: Number(studentRoll),
    }

    if (fatherMobile.trim()) {
      studentDbPayload.father_mobile = Number(fatherMobile.trim())
    }

    const { data: insertedStudent, error: sdError } = await supabase
      .from('student_database')
      .insert(studentDbPayload)
      .select()
      .single()

    if (sdError) {
      alert('Error inserting student in global database: ' + sdError.message)
      setStatus('❌ Insertion failed.')
      return
    }

    // 2. Insert into FMHS_exam_data
    const examDataPayload = {
      exam_id: Number(id),
      iid: insertedStudent.iid,
      class: Number(studentClass),
      section: studentSection.trim(),
      roll: Number(studentRoll),
      student_name_en: studentNameEn.trim(),
      father_name_en: fatherNameEn.trim(),
      exam_name_year: `${exam.exam_name}-${exam.year}`,
      status: 'Pending'
    }

    const { error: edError } = await supabase
      .from('FMHS_exam_data')
      .insert(examDataPayload)

    if (edError) {
      alert('Error adding student to current exam: ' + edError.message)
      setStatus('❌ Insertion failed.')
      return
    }

    setStatus('✅ Student added successfully!')
    setShowManualModal(false)
    setManualForm({
      studentNameEn: '',
      fatherNameEn: '',
      studentClass: '',
      studentSection: '',
      studentRoll: '',
      optionalSubject: '',
      fatherMobile: ''
    })
    
    // Reload counts and table
    await loadMarks()
    await loadMetadata()
    await loadTotalEnrollment()
    setTimeout(() => setStatus(''), 3000)
  }

  function parseCSVContent(text: string) {
    const lines: string[] = []
    let currentLine = ''
    let insideQuote = false

    for (let i = 0; i < text.length; i++) {
      const char = text[i]
      const nextChar = text[i + 1]

      if (char === '"') {
        insideQuote = !insideQuote
      } else if (char === '\r' && nextChar === '\n') {
        if (!insideQuote) {
          lines.push(currentLine)
          currentLine = ''
          i++ // skip \n
        } else {
          currentLine += char
        }
      } else if (char === '\n' || char === '\r') {
        if (!insideQuote) {
          lines.push(currentLine)
          currentLine = ''
        } else {
          currentLine += char
        }
      } else {
        currentLine += char
      }
    }
    if (currentLine) {
      lines.push(currentLine)
    }

    if (lines.length === 0) return []

    // Helper to split line by comma, respecting quotes
    const splitCSVLine = (line: string) => {
      const result: string[] = []
      let cell = ''
      let insideQuote = false
      for (let i = 0; i < line.length; i++) {
        const char = line[i]
        if (char === '"') {
          insideQuote = !insideQuote
        } else if (char === ',' && !insideQuote) {
          result.push(cell.trim())
          cell = ''
        } else {
          cell += char
        }
      }
      result.push(cell.trim())
      return result
    }

    const headers = splitCSVLine(lines[0]).map(h => h.toLowerCase().replace(/['"_\s-]+/g, ''))
    
    const fieldMap: Record<string, string> = {
      studentnameen: 'studentNameEn',
      studentname: 'studentNameEn',
      name: 'studentNameEn',
      fathernameen: 'fatherNameEn',
      fathername: 'fatherNameEn',
      fathersname: 'fatherNameEn',
      class: 'studentClass',
      section: 'studentSection',
      roll: 'studentRoll',
      optionalsubject: 'optionalSubject',
      fourthsubject: 'optionalSubject',
      fathermobile: 'fatherMobile',
      mobile: 'fatherMobile'
    }

    const matchedHeaders = headers.map(h => fieldMap[h] || h)

    const parsedRows = []
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim()
      if (!line) continue
      const cells = splitCSVLine(line)
      const rowData: Record<string, string> = {}
      matchedHeaders.forEach((field, idx) => {
        if (field && cells[idx] !== undefined) {
          rowData[field] = cells[idx]
        }
      })
      
      if (rowData.studentNameEn || rowData.studentRoll) {
        parsedRows.push({
          studentNameEn: rowData.studentNameEn || '',
          fatherNameEn: rowData.fatherNameEn || '',
          studentClass: rowData.studentClass || '',
          studentSection: rowData.studentSection || '',
          studentRoll: rowData.studentRoll || '',
          optionalSubject: rowData.optionalSubject || '',
          fatherMobile: rowData.fatherMobile || ''
        })
      }
    }
    return parsedRows
  }

  function handleCsvFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (event) => {
      const text = event.target?.result as string
      try {
        const rows = parseCSVContent(text)
        if (rows.length === 0) {
          alert('No students found in CSV or headers could not be matched. Make sure to have student name, father name, class, section, and roll columns.')
          return
        }
        setCsvStudents(rows)
      } catch (err: any) {
        alert('Failed to parse CSV file: ' + err.message)
      }
    }
    reader.readAsText(file)
  }

  async function handleImportCsvStudents() {
    if (!exam || csvStudents.length === 0) return

    const invalid = csvStudents.some(s => !s.studentNameEn || !s.fatherNameEn || !s.studentClass || !s.studentSection || !s.studentRoll)
    if (invalid) {
      alert('Some rows have missing required fields (Student Name, Father Name, Class, Section, Roll). Please fix them or upload a clean file.')
      return
    }

    setIsProcessingCsv(true)
    setStatus(`Importing ${csvStudents.length} students...`)

    const sessionYear = exam.year
    
    const dbPayloads = csvStudents.map(s => {
      const payload: Record<string, any> = {
        student_name_en: s.studentNameEn.trim(),
        father_name_en: s.fatherNameEn.trim(),
        optional_subject: s.optionalSubject.trim() || null,
        active_class: Number(s.studentClass),
        active_section: s.studentSection.trim(),
        active_roll: Number(s.studentRoll),
        [`class_${sessionYear}`]: Number(s.studentClass),
        [`section_${sessionYear}`]: s.studentSection.trim(),
        [`roll_${sessionYear}`]: Number(s.studentRoll),
      }
      if (s.fatherMobile.trim()) {
        payload.father_mobile = Number(s.fatherMobile.trim())
      }
      return payload
    })

    const { data: inserted, error: dbErr } = await supabase
      .from('student_database')
      .insert(dbPayloads)
      .select()

    if (dbErr) {
      alert('Error importing to global student database: ' + dbErr.message)
      setIsProcessingCsv(false)
      setStatus('❌ Import failed.')
      return
    }

    const examPayloads = inserted.map((s: any) => ({
      exam_id: Number(id),
      iid: s.iid,
      class: Number(s[`class_${sessionYear}`] ?? s.active_class),
      section: s[`section_${sessionYear}`] ?? s.active_section,
      roll: Number(s[`roll_${sessionYear}`] ?? s.active_roll),
      student_name_en: s.student_name_en,
      father_name_en: s.father_name_en,
      exam_name_year: `${exam.exam_name}-${exam.year}`,
      status: 'Pending'
    }))

    const { error: examErr } = await supabase
      .from('FMHS_exam_data')
      .insert(examPayloads)

    if (examErr) {
      alert('Successfully inserted into global student database but failed to link to the active exam: ' + examErr.message)
      setIsProcessingCsv(false)
      setStatus('⚠️ Partial import failure.')
      return
    }

    setStatus(`✅ Successfully imported ${csvStudents.length} students!`)
    setIsProcessingCsv(false)
    setShowCsvModal(false)
    setCsvStudents([])

    await loadMarks()
    await loadMetadata()
    await loadTotalEnrollment()
    setTimeout(() => setStatus(''), 3000)
  }


  if (loading && !exam) return <div className="spinner" />

  const tabStyle = (tab: string) => ({
    padding: '12px 24px',
    cursor: 'pointer',
    fontWeight: 800,
    fontSize: '14px',
    borderRadius: '12px',
    background: activeTab === tab ? '#4f46e5' : 'transparent',
    color: activeTab === tab ? '#fff' : '#64748b',
    border: 'none',
    transition: '0.2s',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  })

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', color: '#1e293b', fontFamily: "'Outfit', sans-serif" }}>
      
      {/* HEADER */}
      <header style={{ background: '#fff', padding: '16px 40px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 100 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <EmsLogo size={36} />
          <button onClick={() => navigate('/dashboard')} style={{ background: '#f1f5f9', border: '1px solid #e2e8f0', color: '#64748b', padding: '8px 16px', borderRadius: '10px', fontWeight: 700, cursor: 'pointer' }}>← EXIT</button>
          <div>
            <h1 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 900, color: '#0f172a' }}>{exam?.exam_name}</h1>
            <p style={{ margin: 0, fontSize: '11px', color: '#ff5500', fontWeight: 800 }}>ADMIN COMMAND CENTER • {exam?.year} SESSION</p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
           <button onClick={() => navigate('/dashboard')} style={{ padding: '10px 20px', borderRadius: '12px', background: 'linear-gradient(135deg, #2563eb, #1d4ed8)', color: '#fff', border: 'none', fontWeight: 800, boxShadow: '0 4px 10px rgba(37,99,235,0.2)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>⚡ EXAM CONTROL</button>
           <button onClick={saveAllChanges} style={{ padding: '10px 24px', borderRadius: '12px', background: '#059669', color: '#fff', border: 'none', fontWeight: 800, boxShadow: '0 4px 10px rgba(5,150,105,0.2)', cursor: 'pointer' }}>SAVE CHANGES</button>
           <button onClick={() => { setShowUnlockModal(true); loadSubmittedAssignments(); }} style={{ padding: '10px 24px', borderRadius: '12px', background: '#4f46e5', color: '#fff', border: 'none', fontWeight: 800, boxShadow: '0 4px 10px rgba(79,70,229,0.2)', cursor: 'pointer' }}>🔓 UNLOCK SUBMIT</button>
        </div>
      </header>

      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '32px 20px' }}>
        
        {/* TAB NAVIGATION */}
        <div style={{ display: 'flex', gap: '8px', background: '#fff', padding: '6px', borderRadius: '16px', border: '1px solid #e2e8f0', width: 'fit-content', marginBottom: '32px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', flexWrap: 'wrap' }}>
           <button onClick={() => setActiveTab('overview')} style={tabStyle('overview')}>🏠 Overview</button>
           <button onClick={() => setActiveTab('setup')} style={tabStyle('setup')}>⚙️ Setup & Configuration</button>
           <button onClick={() => setActiveTab('marks')} style={tabStyle('marks')}>✍️ Mark Entry</button>
           
           <div style={{ width: '1px', height: '24px', background: '#e2e8f0', alignSelf: 'center', margin: '0 4px' }}></div>
           
           <button onClick={() => navigate(`/subject-gpa/${id}`)} style={tabStyle('')}>🧪 Subject GPA</button>
           <button onClick={() => navigate(`/gpa-final/${id}`)} style={tabStyle('')}>🎯 GPA Final</button>
           
           <div style={{ width: '1px', height: '24px', background: '#e2e8f0', alignSelf: 'center', margin: '0 4px' }}></div>
           
           <button onClick={() => setActiveTab('reports')} style={tabStyle('reports')}>📊 Final Reports</button>
        </div>

        <div style={{ minHeight: '60vh' }}>
            
            {/* ── OVERVIEW TAB ── */}
            {activeTab === 'overview' && (
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px', marginBottom: '40px' }}>
                  <div style={{ padding: '32px', borderRadius: '24px', background: '#fff', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}>
                    <div style={{ fontSize: '11px', fontWeight: 800, color: '#64748b', marginBottom: '8px' }}>LIVE PORTAL STATUS</div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '1.4rem', fontWeight: 900, color: exam?.is_live ? '#059669' : '#f43f5e', transition: 'color 0.2s ease' }}>{exam?.is_live ? 'ACTIVE' : 'OFFLINE'}</span>
                      <div 
                        onClick={() => toggleStatus('is_live', !!exam?.is_live)} 
                        style={{ 
                          width: '56px', 
                          height: '30px', 
                          borderRadius: '999px', 
                          background: exam?.is_live ? '#10b981' : '#cbd5e1', 
                          padding: '3px', 
                          cursor: 'pointer', 
                          position: 'relative', 
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.06)'
                        }}
                      >
                        <div style={{ 
                          width: '24px', 
                          height: '24px', 
                          borderRadius: '50%', 
                          background: '#fff', 
                          position: 'absolute', 
                          left: exam?.is_live ? '29px' : '3px', 
                          top: '3px', 
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                        }} />
                      </div>
                    </div>
                  </div>
                  <div style={{ padding: '32px', borderRadius: '24px', background: '#fff', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}>
                    <div style={{ fontSize: '11px', fontWeight: 800, color: '#64748b', marginBottom: '8px' }}>TEACHER ENTRY ACCESS</div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '1.4rem', fontWeight: 900, color: exam?.teacher_entry_enabled ? '#4f46e5' : '#64748b', transition: 'color 0.2s ease' }}>{exam?.teacher_entry_enabled ? 'ENABLED' : 'DISABLED'}</span>
                      <div 
                        onClick={() => toggleStatus('teacher_entry_enabled', !!exam?.teacher_entry_enabled)} 
                        style={{ 
                          width: '56px', 
                          height: '30px', 
                          borderRadius: '999px', 
                          background: exam?.teacher_entry_enabled ? '#4f46e5' : '#cbd5e1', 
                          padding: '3px', 
                          cursor: 'pointer', 
                          position: 'relative', 
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.06)'
                        }}
                      >
                        <div style={{ 
                          width: '24px', 
                          height: '24px', 
                          borderRadius: '50%', 
                          background: '#fff', 
                          position: 'absolute', 
                          left: exam?.teacher_entry_enabled ? '29px' : '3px', 
                          top: '3px', 
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                        }} />
                      </div>
                    </div>
                  </div>
                  <div style={{ padding: '32px', borderRadius: '24px', background: '#fff', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}>
                    <div style={{ fontSize: '11px', fontWeight: 800, color: '#64748b', marginBottom: '8px' }}>STUDENT ACCESS</div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '1.4rem', fontWeight: 900, color: exam?.student_result_live ? '#059669' : '#f43f5e', transition: 'color 0.2s ease' }}>{exam?.student_result_live ? 'PUBLISHED' : 'HIDDEN'}</span>
                      <div 
                        onClick={() => toggleStatus('student_result_live', !!exam?.student_result_live)} 
                        style={{ 
                          width: '56px', 
                          height: '30px', 
                          borderRadius: '999px', 
                          background: exam?.student_result_live ? '#059669' : '#cbd5e1', 
                          padding: '3px', 
                          cursor: 'pointer', 
                          position: 'relative', 
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.06)'
                        }}
                      >
                        <div style={{ 
                          width: '24px', 
                          height: '24px', 
                          borderRadius: '50%', 
                          background: '#fff', 
                          position: 'absolute', 
                          left: exam?.student_result_live ? '29px' : '3px', 
                          top: '3px', 
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                        }} />
                      </div>
                    </div>
                  </div>
                  <div style={{ padding: '32px', borderRadius: '24px', background: '#fff', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}>
                    <div style={{ fontSize: '11px', fontWeight: 800, color: '#64748b', marginBottom: '8px' }}>TOTAL ENROLLMENT</div>
                    <div style={{ fontSize: '1.4rem', fontWeight: 900 }}>{totalEnrolled !== null ? totalEnrolled : '...'} Students</div>
                  </div>
                </div>

                <div style={{ background: '#fff', borderRadius: '32px', border: '1px solid #e2e8f0', padding: '40px', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.05)' }}>
                  <h3 style={{ margin: '0 0 32px 0', fontWeight: 900, fontSize: '1.5rem' }}>Administrative Workflow Guide</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '32px' }}>
                    {[
                      { step: 1, title: 'Import Students', desc: 'Load student data from global database.', active: true },
                      { step: 2, title: 'Configure Subjects', desc: 'Define pass marks and full marks.', active: subjectRules.length > 0 },
                      { step: 3, title: 'Teacher Access', desc: 'Assign subjects to respective teachers.', active: true },
                      { step: 4, title: 'Mark Entry', desc: 'Fill in marks via Admin or Teacher portal.', active: data.length > 0 },
                      { step: 5, title: 'Process GPA', desc: 'Run the engine to calculate final grades.', active: true }
                    ].map(s => (
                      <div key={s.step} style={{ position: 'relative' }}>
                        <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: s.active ? '#4f46e5' : '#f1f5f9', color: s.active ? '#fff' : '#94a3b8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, marginBottom: '16px' }}>{s.step}</div>
                        <h4 style={{ margin: '0 0 8px 0', fontSize: '1rem', fontWeight: 800 }}>{s.title}</h4>
                        <p style={{ margin: 0, fontSize: '13px', color: '#64748b', lineHeight: '1.5' }}>{s.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ── SETUP TAB ── */}
            {activeTab === 'setup' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {/* Upper Section: 2 Columns */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  
                  {/* CARD 1: LOAD & IMPORT STUDENTS */}
                  <div style={{ background: '#fff', padding: '20px 24px', borderRadius: '20px', border: '1px solid #e2e8f0', boxShadow: '0 2px 10px rgba(0,0,0,0.03)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                      <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: '#e0e7ff', color: '#4338ca', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: '13px' }}>1</div>
                      <h3 style={{ margin: 0, fontWeight: 900, fontSize: '1.05rem', color: '#0f172a' }}>Load & Import Students</h3>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                      <div>
                        <label style={{ fontSize: '10px', fontWeight: 800, color: '#64748b', display: 'block', marginBottom: '4px', textTransform: 'uppercase' }}>SOURCE YEAR</label>
                        <select className="form-control" style={{ borderRadius: '10px', padding: '8px 12px', fontSize: '12px', fontWeight: 700 }} value={sourceYear} onChange={e => setSourceYear(e.target.value)}>
                          <option value="">Select Year</option>
                          {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
                        </select>
                      </div>
                      <div>
                        <label style={{ fontSize: '10px', fontWeight: 800, color: '#64748b', display: 'block', marginBottom: '4px', textTransform: 'uppercase' }}>TARGET CLASS</label>
                        <select className="form-control" style={{ borderRadius: '10px', padding: '8px 12px', fontSize: '12px', fontWeight: 700 }} value={gridClass} onChange={e => setGridClass(e.target.value)}>
                          <option value="">Select Class</option>
                          {sourceClasses.map(c => <option key={c} value={c}>Class {c}</option>)}
                        </select>
                      </div>
                    </div>

                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ fontSize: '10px', fontWeight: 800, color: '#64748b', display: 'block', marginBottom: '4px', textTransform: 'uppercase' }}>TARGET SECTION</label>
                      <select className="form-control" style={{ borderRadius: '10px', padding: '8px 12px', fontSize: '12px', fontWeight: 700 }} value={gridSection} onChange={e => setGridSection(e.target.value)}>
                        <option value="All">All Sections</option>
                        {(sourceSectionsByClass[gridClass] || []).map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>

                    <button onClick={importStudents} style={{ width: '100%', padding: '10px', borderRadius: '12px', background: 'linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)', color: '#fff', border: 'none', fontWeight: 800, fontSize: '12px', boxShadow: '0 2px 8px rgba(79, 70, 229, 0.25)', cursor: 'pointer', marginBottom: '14px' }}>
                      📥 IMPORT {previewCount !== null ? `${previewCount} ` : ''}STUDENTS
                    </button>

                    <div style={{ margin: '12px 0', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <div style={{ position: 'absolute', width: '100%', height: '1px', background: '#e2e8f0' }}></div>
                      <span style={{ position: 'relative', background: '#fff', padding: '0 12px', fontSize: '10px', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Or Add Manually</span>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                      <button 
                        onClick={() => setShowManualModal(true)}
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', padding: '8px 12px', borderRadius: '10px', background: '#fff', border: '1.5px solid #cbd5e1', color: '#334155', fontWeight: 800, fontSize: '12px', cursor: 'pointer', transition: 'all 0.2s' }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = '#4f46e5'; e.currentTarget.style.color = '#4f46e5'; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.color = '#334155'; }}
                      >
                        ✏️ Add Manually
                      </button>
                      <button 
                        onClick={() => setShowCsvModal(true)}
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', padding: '8px 12px', borderRadius: '10px', background: '#fff', border: '1.5px solid #cbd5e1', color: '#334155', fontWeight: 800, fontSize: '12px', cursor: 'pointer', transition: 'all 0.2s' }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = '#4f46e5'; e.currentTarget.style.color = '#4f46e5'; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.color = '#334155'; }}
                      >
                        📤 Upload CSV
                      </button>
                    </div>
                  </div>

                  {/* RIGHT COLUMN: 2. SUBJECT RULES & 3. TEACHER ACCESS */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    
                    {/* CARD 2: SUBJECT RULES */}
                    <div style={{ background: '#fff', padding: '20px 24px', borderRadius: '20px', border: '1px solid #e2e8f0', boxShadow: '0 2px 10px rgba(0,0,0,0.03)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: '#dcfce7', color: '#15803d', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: '13px' }}>2</div>
                          <h3 style={{ margin: 0, fontWeight: 900, fontSize: '1.05rem', color: '#0f172a' }}>Subject Rules</h3>
                        </div>
                        <span style={{ background: '#f1f5f9', border: '1px solid #cbd5e1', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 800, color: '#475569' }}>
                          {subjectRules.length} Configured
                        </span>
                      </div>
                      <p style={{ color: '#64748b', fontSize: '12px', margin: '0 0 14px 0' }}>Define which subjects belong to this exam and set pass & total mark criteria.</p>
                      <button 
                        onClick={() => navigate(`/exam-subjects/${id}`)} 
                        style={{ width: '100%', padding: '10px', borderRadius: '12px', background: '#f8fafc', border: '1.5px solid #cbd5e1', fontWeight: 800, fontSize: '12px', color: '#0f172a', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
                      >
                        📐 MANAGE SUBJECT RULES
                      </button>
                    </div>

                    {/* CARD 3: TEACHER ACCESS & PROGRESS */}
                    <div style={{ background: '#fff', padding: '20px 24px', borderRadius: '20px', border: '1px solid #e2e8f0', boxShadow: '0 2px 10px rgba(0,0,0,0.03)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: '#e0f2fe', color: '#0369a1', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: '13px' }}>3</div>
                        <h3 style={{ margin: 0, fontWeight: 900, fontSize: '1.05rem', color: '#0f172a' }}>Teacher Access & Permissions</h3>
                      </div>
                      <p style={{ color: '#64748b', fontSize: '12px', margin: '0 0 14px 0' }}>Grant teacher access for classes & subjects and track mark entry progress list.</p>
                      
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                        <button 
                          onClick={() => navigate(`/exam-teachers/${id}`)} 
                          style={{ width: '100%', padding: '10px', borderRadius: '12px', background: '#f8fafc', border: '1.5px solid #cbd5e1', fontWeight: 800, fontSize: '12px', color: '#0f172a', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
                        >
                          ⚙️ Configure Access
                        </button>
                        <button 
                          onClick={() => navigate(`/teacher_access_list/${id}`)} 
                          style={{ width: '100%', padding: '10px', borderRadius: '12px', background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)', color: '#ffffff', border: 'none', fontWeight: 800, fontSize: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', boxShadow: '0 2px 8px rgba(2, 132, 199, 0.25)' }}
                        >
                          📋 Teacher Access List
                        </button>
                      </div>
                    </div>

                  </div>
                </div>

                {/* LOWER SECTION: 4. TOTAL SUBJECTS CONFIGURATION (GPA DIVISORS) */}
                <div style={{ background: '#fff', padding: '20px 24px', borderRadius: '20px', border: '1px solid #e2e8f0', boxShadow: '0 2px 10px rgba(0,0,0,0.03)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: '#fef3c7', color: '#b45309', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: '13px' }}>4</div>
                      <h3 style={{ margin: 0, fontWeight: 900, fontSize: '1.05rem', color: '#0f172a' }}>Total Subjects Configuration (GPA Divisors)</h3>
                    </div>
                    {!isEditingCounts ? (
                      <button onClick={() => setIsEditingCounts(true)} style={{ padding: '6px 14px', borderRadius: '10px', background: '#f1f5f9', border: '1px solid #cbd5e1', fontWeight: 800, fontSize: '12px', color: '#4f46e5', cursor: 'pointer' }}>✏️ Edit Settings</button>
                    ) : (
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button onClick={() => { setIsEditingCounts(false); loadExamData(); }} style={{ padding: '6px 14px', borderRadius: '10px', background: '#fff', border: '1px solid #cbd5e1', fontWeight: 800, fontSize: '12px', color: '#64748b', cursor: 'pointer' }}>Cancel</button>
                        <button onClick={saveClassCounts} style={{ padding: '6px 16px', borderRadius: '10px', background: '#059669', border: 'none', fontWeight: 800, fontSize: '12px', color: '#fff', cursor: 'pointer', boxShadow: '0 2px 8px rgba(5,150,105,0.2)' }}>Save Configuration</button>
                      </div>
                    )}
                  </div>
                  <p style={{ color: '#64748b', fontSize: '12px', margin: '0 0 14px 0' }}>GPA বের করার জন্য যত দিয়ে ভাগ হবে when calculating the final GPA for each class.</p>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: '12px' }}>
                    {(() => {
                      const activeClasses = (availableClasses.length > 0 
                        ? availableClasses.map(Number)
                        : (subjectRules.length > 0
                            ? Array.from(new Set(subjectRules.flatMap(r => (r.exam_class || []).filter((c: any) => c.selected).map((c: any) => Number(c.class))))).filter(n => !isNaN(n) && n > 0)
                            : [6, 7, 8, 9, 10, 11, 12]
                          )
                      ).sort((a, b) => a - b)

                      return activeClasses.map(c => (
                        <div key={c} style={{ background: isEditingCounts ? '#fff' : '#f8fafc', padding: '10px', borderRadius: '12px', border: isEditingCounts ? '2px solid #4f46e5' : '1px solid #e2e8f0', transition: '0.2s', textAlign: 'center' }}>
                          <div style={{ fontSize: '10px', fontWeight: 900, color: '#64748b', marginBottom: '4px' }}>CLASS {c}</div>
                          {isEditingCounts ? (
                            <input 
                              type="number" 
                              className="form-control" 
                              style={{ textAlign: 'center', fontWeight: 900, fontSize: '1.1rem', borderRadius: '8px', border: '1px solid #cbd5e1', padding: '4px' }}
                              value={draftCounts[c] || 0}
                              onChange={(e) => setDraftCounts({ ...draftCounts, [c]: Number(e.target.value) })}
                            />
                          ) : (
                            <div style={{ fontWeight: 900, fontSize: '1.3rem', color: '#0f172a' }}>
                              {exam ? (exam as any)[`class_${c}`] : 0}
                            </div>
                          )}
                        </div>
                      ))
                    })()}
                  </div>
                </div>
              </div>
            )}

            {/* ── MARKS TAB ── */}
            {activeTab === 'marks' && (
              <div style={{ background: '#fff', borderRadius: '32px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.05)' }}>
                <div style={{ padding: '32px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                   <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', alignItems: 'flex-end' }}>
                      <div style={{ flex: 1, minWidth: '180px' }}>
                        <label style={{ display: 'block', fontSize: '11px', fontWeight: 800, color: '#64748b', marginBottom: '8px', textTransform: 'uppercase' }}>1. Select Class</label>
                        <select className="form-control" style={{ borderRadius: '14px', padding: '12px', fontWeight: 700 }} value={gridClass} onChange={e => { setGridClass(e.target.value); setGridSection('All'); setFilterSubject(''); setData([]); }}>
                          <option value="">Choose Class...</option>
                          {availableClasses.map(c => <option key={c} value={c}>Class {c}</option>)}
                        </select>
                      </div>
                      <div style={{ flex: 1, minWidth: '180px' }}>
                        <label style={{ display: 'block', fontSize: '11px', fontWeight: 800, color: '#64748b', marginBottom: '8px', textTransform: 'uppercase' }}>2. Select Section</label>
                        <select className="form-control" style={{ borderRadius: '14px', padding: '12px', fontWeight: 700 }} value={gridSection} onChange={e => { setGridSection(e.target.value); setFilterSubject(''); setData([]); }}>
                          <option value="All">All Sections</option>
                          {(classSectionsMap[gridClass] || []).map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                      <div style={{ flex: 2, minWidth: '220px' }}>
                        <label style={{ display: 'block', fontSize: '11px', fontWeight: 800, color: '#64748b', marginBottom: '8px', textTransform: 'uppercase' }}>3. Select Subject (Auto-Load)</label>
                        <select 
                          className="form-control" 
                          disabled={!gridClass}
                          style={{ borderRadius: '14px', padding: '12px', fontWeight: 700, border: filterSubject ? '2px solid #4f46e5' : '1px solid #e2e8f0', background: !gridClass ? '#f1f5f9' : '#fff' }} 
                          value={filterSubject} 
                          onChange={e => { 
                            setFilterSubject(e.target.value); 
                            if (e.target.value) {
                                // We need to pass the values directly because state update is async
                                loadMarksExplicitly(gridClass, gridSection, e.target.value);
                            } else {
                                setData([]);
                            }
                          }}
                        >
                          <option value="">-- Choose Subject to Start Entry --</option>
                          {Object.keys(subjectMap).map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                   </div>
                   
                   {loading && (
                     <div style={{ marginTop: '20px', display: 'flex', alignItems: 'center', gap: '10px', color: '#4f46e5', fontWeight: 700 }}>
                        <div className="spinner-small" style={{ width: '16px', height: '16px', border: '2px solid #eef2ff', borderTopColor: '#4f46e5', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }}></div>
                        <span>Loading student data...</span>
                     </div>
                   )}

                   {data.length > 0 && filterSubject && !loading && (
                     <div style={{ marginTop: '24px', display: 'flex', gap: '12px', alignItems: 'center', background: '#fff', padding: '12px 20px', borderRadius: '16px', border: '1px solid #e2e8f0', width: '100%', justifyContent: 'space-between', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#10b981' }}></div>
                          <span style={{ fontSize: '13px', fontWeight: 800, color: '#0f172a' }}>
                            Editing: <span style={{ color: '#4f46e5' }}>{filterSubject}</span> | Class {gridClass} ({gridSection})
                          </span>
                          <div style={{ width: '1px', height: '16px', background: '#e2e8f0', margin: '0 8px' }}></div>
                          <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 600 }}>{data.length} Students Loaded</span>
                        </div>
                        <div style={{ display: 'flex', gap: '12px' }}>
                          <button 
                             onClick={resetLocalChanges}
                             style={{ padding: '8px 16px', borderRadius: '12px', border: '1px solid #fee2e2', background: '#fff', color: '#ef4444', fontSize: '11px', fontWeight: 800, cursor: 'pointer', transition: '0.2s' }}
                          >
                             🔄 RESET CHANGES
                          </button>
                          <button 
                             onClick={() => setShowDetails(!showDetails)}
                             style={{ padding: '8px 16px', borderRadius: '12px', border: '1px solid #e2e8f0', background: showDetails ? '#f8fafc' : '#fff', color: '#64748b', fontSize: '11px', fontWeight: 800, cursor: 'pointer', transition: '0.2s' }}
                          >
                             {showDetails ? '🙈 HIDE NAMES' : '👁️ SHOW NAMES'}
                          </button>
                        </div>
                     </div>
                   )}
                </div>

                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                      <tr>
                        {fixedCols.map((c: string) => <th key={c} style={{ padding: '16px', textAlign: 'left', fontWeight: 900, textTransform: 'uppercase', fontSize: '11px', color: '#64748b' }}>{c}</th>)}

                        {showDetails && (
                          <>
                            <th style={{ padding: '16px', textAlign: 'left', fontWeight: 900, textTransform: 'uppercase', fontSize: '11px', color: '#64748b' }}>STUDENT NAME</th>
                            <th style={{ padding: '16px', textAlign: 'left', fontWeight: 900, textTransform: 'uppercase', fontSize: '11px', color: '#64748b' }}>IID</th>
                          </>
                        )}
                        {filterSubject ? (() => {
                          const rule = subjectRules.find(r => r.subject_name === filterSubject);
                          return (
                            <>
                              {subjectMap[filterSubject].CQ && (
                                <th style={{ padding: '16px', textAlign: 'center', fontSize: '11px' }}>
                                  CQ <div style={{ fontSize: '9px', color: '#ef4444', fontWeight: 800 }}>P: {rule?.pass_cq}</div>
                                </th>
                              )}
                              {subjectMap[filterSubject].MCQ && (
                                <th style={{ padding: '16px', textAlign: 'center', fontSize: '11px' }}>
                                  MCQ <div style={{ fontSize: '9px', color: '#ef4444', fontWeight: 800 }}>P: {rule?.pass_mcq}</div>
                                </th>
                              )}
                              {subjectMap[filterSubject].Practical && (
                                <th style={{ padding: '16px', textAlign: 'center', fontSize: '11px' }}>
                                  PRAC <div style={{ fontSize: '9px', color: '#ef4444', fontWeight: 800 }}>P: {rule?.pass_practical}</div>
                                </th>
                              )}
                              <th style={{ padding: '16px', textAlign: 'center', background: '#eef2ff', fontSize: '11px' }}>
                                TOTAL <div style={{ fontSize: '9px', color: '#4f46e5', fontWeight: 800 }}>P: {rule?.pass_total}</div>
                              </th>
                              <th style={{ padding: '16px', textAlign: 'center', color: '#64748b', fontSize: '11px', fontWeight: 800 }}>ACTION</th>
                            </>
                          );
                        })() : (
                          Object.keys(subjectMap).map(s => <th key={s} style={{ padding: '16px', textAlign: 'center', minWidth: '100px', fontWeight: 900, textTransform: 'uppercase', fontSize: '11px', color: '#64748b' }}>{s}</th>)
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {data.map(row => (
                        <tr key={String(row.id)} style={{ borderBottom: '1px solid #f1f5f9', transition: '0.1s' }}>
                          {fixedCols.map((c: string) => <td key={c} style={{ padding: '16px', fontWeight: 900 }}>{String(row[c] ?? '-')}</td>)}
                          {showDetails && (
                            <>
                              <td style={{ padding: '16px', fontWeight: 600 }}>{String(row.student_name_en ?? '-')}</td>
                              <td style={{ padding: '16px', fontSize: '11px', color: '#64748b' }}>{String(row.iid ?? '-')}</td>
                            </>
                          )}
                          {filterSubject ? (
                            <>
                              {subjectMap[filterSubject].CQ && (
                                <td style={{ padding: '8px', textAlign: 'center' }}>
                                  <input 
                                    type="number" 
                                    value={String(row[subjectMap[filterSubject].CQ!] ?? '')} 
                                    onChange={e => {
                                      const val = e.target.value;
                                      handleEdit(String(row.id), subjectMap[filterSubject].CQ!, val);
                                      const newData = [...data];
                                      const idx = newData.findIndex(r => String(r.id) === String(row.id));
                                      if (idx !== -1) {
                                        newData[idx][subjectMap[filterSubject].CQ!] = val === '' ? null : Number(val);
                                        setData(newData);
                                      }
                                    }} 
                                    style={{ width: '70px', padding: '8px', textAlign: 'center', borderRadius: '8px', border: '1px solid #e2e8f0' }} 
                                  />
                                </td>
                              )}
                              {subjectMap[filterSubject].MCQ && (
                                <td style={{ padding: '8px', textAlign: 'center' }}>
                                  <input 
                                    type="number" 
                                    value={String(row[subjectMap[filterSubject].MCQ!] ?? '')} 
                                    onChange={e => {
                                      const val = e.target.value;
                                      handleEdit(String(row.id), subjectMap[filterSubject].MCQ!, val);
                                      const newData = [...data];
                                      const idx = newData.findIndex(r => String(r.id) === String(row.id));
                                      if (idx !== -1) {
                                        newData[idx][subjectMap[filterSubject].MCQ!] = val === '' ? null : Number(val);
                                        setData(newData);
                                      }
                                    }} 
                                    style={{ width: '70px', padding: '8px', textAlign: 'center', borderRadius: '8px', border: '1px solid #e2e8f0' }} 
                                  />
                                </td>
                              )}
                              {subjectMap[filterSubject].Practical && (
                                <td style={{ padding: '8px', textAlign: 'center' }}>
                                  <input 
                                    type="number" 
                                    value={String(row[subjectMap[filterSubject].Practical!] ?? '')} 
                                    onChange={e => {
                                      const val = e.target.value;
                                      handleEdit(String(row.id), subjectMap[filterSubject].Practical!, val);
                                      const newData = [...data];
                                      const idx = newData.findIndex(r => String(r.id) === String(row.id));
                                      if (idx !== -1) {
                                        newData[idx][subjectMap[filterSubject].Practical!] = val === '' ? null : Number(val);
                                        setData(newData);
                                      }
                                    }} 
                                    style={{ width: '70px', padding: '8px', textAlign: 'center', borderRadius: '8px', border: '1px solid #e2e8f0' }} 
                                  />
                                </td>
                              )}
                              <td style={{ padding: '16px', textAlign: 'center', fontWeight: 900, color: '#4f46e5', background: '#f5f7ff' }}>{String(row[subjectMap[filterSubject].Total!] ?? '-')}</td>
                              <td style={{ padding: '12px', textAlign: 'center' }}>
                                <button
                                  onClick={() => saveRow(String(row.id))}
                                  disabled={savingRows[String(row.id)] === 'saving'}
                                  style={{
                                    padding: '8px 16px',
                                    borderRadius: '10px',
                                    border: 'none',
                                    fontWeight: 800,
                                    fontSize: '11px',
                                    cursor: 'pointer',
                                    transition: '0.2s',
                                    background: 
                                      savingRows[String(row.id)] === 'pending' ? '#f59e0b' : 
                                      savingRows[String(row.id)] === 'success' ? '#10b981' : 
                                      savingRows[String(row.id)] === 'saving' ? '#94a3b8' : '#f1f5f9',
                                    color: (savingRows[String(row.id)] === 'pending' || savingRows[String(row.id)] === 'success' || savingRows[String(row.id)] === 'saving') ? '#fff' : '#64748b'
                                  }}
                                >
                                  {savingRows[String(row.id)] === 'saving' ? '...' : 
                                   savingRows[String(row.id)] === 'success' ? 'SAVED ✅' : 
                                   savingRows[String(row.id)] === 'pending' ? 'SAVE' : 'SAVE'}
                                </button>
                              </td>
                            </>
                          ) : Object.keys(subjectMap).map(s => {
                            return (
                              <td key={s} style={{ padding: '16px', textAlign: 'center' }}>
                                <span style={{ fontWeight: 800, color: row[`*${s}_GPA`] === 'F' ? '#ef4444' : '#059669', fontSize: '14px' }}>
                                  {String(row[`*${s}_Total`] ?? '-')}
                                </span>
                                {String(row[`*${s}_GPA`] ?? '') && <span style={{ fontSize: '11px', color: '#94a3b8', display: 'block', marginTop: '4px' }}>Grade: {String(row[`*${s}_GPA`])}</span>}
                              </td>
                            )
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ── REPORTS TAB ── */}
            {activeTab === 'reports' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                <div style={{ padding: '32px', borderRadius: '24px', background: '#fff', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}>
                  <h3 style={{ margin: '0 0 24px 0', fontSize: '1.4rem', fontWeight: 900 }}>📊 Live Session Summary</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px' }}>
                    <div style={{ background: '#f8fafc', padding: '20px', borderRadius: '20px', border: '1px solid #e2e8f0' }}>
                      <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 800 }}>TOTAL STUDENTS</div>
                      <div style={{ fontSize: '28px', fontWeight: 900, marginTop: '4px' }}>{data.length}</div>
                    </div>
                    <div style={{ background: '#dcfce7', padding: '20px', borderRadius: '20px', border: '1px solid #bbf7d0' }}>
                      <div style={{ fontSize: '11px', color: '#15803d', fontWeight: 800 }}>PASSED</div>
                      <div style={{ fontSize: '28px', fontWeight: 900, color: '#15803d', marginTop: '4px' }}>{summaryData.passCount}</div>
                    </div>
                    <div style={{ background: '#fee2e2', padding: '20px', borderRadius: '20px', border: '1px solid #fecaca' }}>
                      <div style={{ fontSize: '11px', color: '#b91c1c', fontWeight: 800 }}>FAILED</div>
                      <div style={{ fontSize: '28px', fontWeight: 900, color: '#b91c1c', marginTop: '4px' }}>{summaryData.failCount}</div>
                    </div>
                    <div style={{ background: '#e0e7ff', padding: '20px', borderRadius: '20px', border: '1px solid #c7d2fe' }}>
                      <div style={{ fontSize: '11px', color: '#4338ca', fontWeight: 800 }}>TOP PERFORMERS</div>
                      <div style={{ fontSize: '14px', marginTop: '8px' }}>
                        {summaryData.topStudents.map((s, idx) => (
                          <div key={idx} style={{ color: '#1e293b', fontSize: '11px', fontWeight: 700 }}>#{idx+1} {String(s.student_name_en || '').split(' ')[0]} ({String(s.total_mark ?? '')})</div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* ELEGANT FULL-WIDTH REPORTS CARDS GRID */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '20px' }}>
                  {/* 1. Mark Sheet */}
                  <div
                    style={{
                      padding: '24px',
                      borderRadius: '24px',
                      background: '#ffffff',
                      border: '1.5px solid #e2e8f0',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      gap: '20px',
                    }}
                  >
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '14px' }}>
                        <div style={{ width: '46px', height: '46px', borderRadius: '14px', background: '#e0e7ff', color: '#4338ca', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', flexShrink: 0 }}>
                          🏆
                        </div>
                        <div>
                          <h4 style={{ margin: 0, fontWeight: 900, fontSize: '1.1rem', color: '#0f172a' }}>Mark Sheet</h4>
                          <span style={{ fontSize: '10px', fontWeight: 800, color: '#6366f1', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Merit List</span>
                        </div>
                      </div>
                      <p style={{ color: '#64748b', fontSize: '13px', margin: 0, lineHeight: 1.5 }}>
                        Complete merit list with student GPAs, rank, and total marks.
                      </p>
                    </div>
                    <button
                      onClick={() => navigate(`/result-list/${id}`)}
                      style={{
                        width: '100%',
                        padding: '11px 16px',
                        borderRadius: '12px',
                        background: '#4f46e5',
                        border: 'none',
                        fontWeight: 800,
                        color: '#ffffff',
                        fontSize: '13px',
                        cursor: 'pointer',
                        boxShadow: '0 4px 12px rgba(79,70,229,0.25)',
                      }}
                    >
                      Open Merit List →
                    </button>
                  </div>

                  {/* 2. Failure Analytics */}
                  <div
                    style={{
                      padding: '24px',
                      borderRadius: '24px',
                      background: '#ffffff',
                      border: '1.5px solid #e2e8f0',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      gap: '20px',
                    }}
                  >
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '14px' }}>
                        <div style={{ width: '46px', height: '46px', borderRadius: '14px', background: '#fee2e2', color: '#b91c1c', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', flexShrink: 0 }}>
                          ❌
                        </div>
                        <div>
                          <h4 style={{ margin: 0, fontWeight: 900, fontSize: '1.1rem', color: '#0f172a' }}>Failure Analytics</h4>
                          <span style={{ fontSize: '10px', fontWeight: 800, color: '#ef4444', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Fail Report</span>
                        </div>
                      </div>
                      <p style={{ color: '#64748b', fontSize: '13px', margin: 0, lineHeight: 1.5 }}>
                        Identify students who failed one or more subjects with details.
                      </p>
                    </div>
                    <button
                      onClick={() => navigate(`/fail-report/${id}`)}
                      style={{
                        width: '100%',
                        padding: '11px 16px',
                        borderRadius: '12px',
                        background: '#f8fafc',
                        border: '1.5px solid #cbd5e1',
                        fontWeight: 800,
                        color: '#334155',
                        fontSize: '13px',
                        cursor: 'pointer',
                      }}
                    >
                      View Fail Report →
                    </button>
                  </div>

                  {/* 3. Bulk Print */}
                  <div
                    style={{
                      padding: '24px',
                      borderRadius: '24px',
                      background: '#ffffff',
                      border: '1.5px solid #e2e8f0',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      gap: '20px',
                    }}
                  >
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '14px' }}>
                        <div style={{ width: '46px', height: '46px', borderRadius: '14px', background: '#e0f2fe', color: '#0369a1', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', flexShrink: 0 }}>
                          🖨️
                        </div>
                        <div>
                          <h4 style={{ margin: 0, fontWeight: 900, fontSize: '1.1rem', color: '#0f172a' }}>Bulk Print Center</h4>
                          <span style={{ fontSize: '10px', fontWeight: 800, color: '#0284c7', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Print Hub</span>
                        </div>
                      </div>
                      <p style={{ color: '#64748b', fontSize: '13px', margin: 0, lineHeight: 1.5 }}>
                        Generate and batch print marksheets for entire session.
                      </p>
                    </div>
                    <button
                      onClick={() => navigate(`/print-results/${id}`)}
                      style={{
                        width: '100%',
                        padding: '11px 16px',
                        borderRadius: '12px',
                        background: '#f8fafc',
                        border: '1.5px solid #cbd5e1',
                        fontWeight: 800,
                        color: '#334155',
                        fontSize: '13px',
                        cursor: 'pointer',
                      }}
                    >
                      Print Center →
                    </button>
                  </div>

                  {/* 4. General Summary */}
                  <div
                    style={{
                      padding: '24px',
                      borderRadius: '24px',
                      background: '#ffffff',
                      border: '1.5px solid #e2e8f0',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      gap: '20px',
                    }}
                  >
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '14px' }}>
                        <div style={{ width: '46px', height: '46px', borderRadius: '14px', background: '#fef3c7', color: '#b45309', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', flexShrink: 0 }}>
                          📋
                        </div>
                        <div>
                          <h4 style={{ margin: 0, fontWeight: 900, fontSize: '1.1rem', color: '#0f172a' }}>General Summary</h4>
                          <span style={{ fontSize: '10px', fontWeight: 800, color: '#d97706', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Overview</span>
                        </div>
                      </div>
                      <p style={{ color: '#64748b', fontSize: '13px', margin: 0, lineHeight: 1.5 }}>
                        High-level statistical overview and session summary.
                      </p>
                    </div>
                    <button
                      onClick={() => navigate(`/summary`)}
                      style={{
                        width: '100%',
                        padding: '11px 16px',
                        borderRadius: '12px',
                        background: '#f8fafc',
                        border: '1.5px solid #cbd5e1',
                        fontWeight: 800,
                        color: '#334155',
                        fontSize: '13px',
                        cursor: 'pointer',
                      }}
                    >
                      View Summary →
                    </button>
                  </div>

                  {/* 5. Full SMS Gateway (Password 112233) */}
                  <div
                    style={{
                      padding: '24px',
                      borderRadius: '24px',
                      background: '#ffffff',
                      border: '1.5px solid #e2e8f0',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      gap: '20px',
                    }}
                  >
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '14px' }}>
                        <div style={{ width: '46px', height: '46px', borderRadius: '14px', background: '#fce7f3', color: '#be185d', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', flexShrink: 0 }}>
                          ✉️
                        </div>
                        <div>
                          <h4 style={{ margin: 0, fontWeight: 900, fontSize: '1.1rem', color: '#0f172a' }}>Full SMS Gateway</h4>
                          <span style={{ fontSize: '10px', fontWeight: 800, color: '#db2777', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Password Required 🔒</span>
                        </div>
                      </div>
                      <p style={{ color: '#64748b', fontSize: '13px', margin: 0, lineHeight: 1.5 }}>
                        Advanced SMS messaging system with delivery logs & formatters.
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        const pass = prompt('🔒 Enter Password for Full SMS Gateway:')
                        if (pass === '112233') {
                          sessionStorage.setItem('sms_gateway_auth', '112233')
                          navigate(`/sms-full/${id}`)
                        } else if (pass !== null) {
                          alert('❌ Incorrect password!')
                        }
                      }}
                      style={{
                        width: '100%',
                        padding: '11px 16px',
                        borderRadius: '12px',
                        background: '#4f46e5',
                        border: 'none',
                        fontWeight: 800,
                        color: '#ffffff',
                        fontSize: '13px',
                        cursor: 'pointer',
                        boxShadow: '0 4px 12px rgba(79,70,229,0.25)',
                      }}
                    >
                      Open Gateway 🔒
                    </button>
                  </div>
                </div>
              </div>
            )}
        </div>

        {status && (
          <div style={{ position: 'fixed', bottom: '24px', right: '40px', background: '#0f172a', color: '#fff', padding: '12px 24px', borderRadius: '12px', boxShadow: '0 10px 30px rgba(0,0,0,0.2)', fontSize: '14px', fontWeight: 600, zIndex: 1000 }}>{status}</div>
        )}

        {/* EDIT SUBJECT MODAL */}
        {editingRule && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)' }}>
            <div style={{ background: '#fff', borderRadius: '32px', padding: '40px', width: '500px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }}>
              <h3 style={{ margin: '0 0 8px 0', fontSize: '1.5rem', fontWeight: 900 }}>Edit {editingRule.subject_name} Rules</h3>
              <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '32px' }}>Update pass marks for this subject.</p>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '32px' }}>
                {[
                  { label: 'Full Marks', key: 'full_marks' },
                  { label: 'Pass CQ', key: 'pass_cq' },
                  { label: 'Pass MCQ', key: 'pass_mcq' },
                  { label: 'Pass Practical', key: 'pass_practical' },
                  { label: 'Total Pass', key: 'pass_total' }
                ].map(f => (
                  <div key={f.key}>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: 800, color: '#94a3b8', marginBottom: '6px' }}>{f.label.toUpperCase()}</label>
                    <input type="number" className="form-control" style={{ borderRadius: '12px', padding: '12px' }} value={String((editingRule as any)[f.key] ?? '')} onChange={e => setEditingRule({ ...editingRule, [f.key]: Number(e.target.value) })} />
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button className="btn btn-primary" style={{ flex: 2, borderRadius: '16px', padding: '16px', background: '#4f46e5', border: 'none', fontWeight: 800, color: '#fff' }} onClick={async () => {
                  const { error } = await supabase.from('FMHS_exam_subjects').update(editingRule).eq('id', editingRule.id)
                  if (error) alert(error.message)
                  else { setEditingRule(null); loadExamData() }
                }}>Save Changes</button>
                <button className="btn btn-secondary" style={{ flex: 1, borderRadius: '16px', padding: '16px', background: '#f1f5f9', border: 'none', fontWeight: 700, color: '#475569' }} onClick={() => setEditingRule(null)}>Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* MANUAL STUDENT MODAL */}
        {showManualModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)' }}>
            <div style={{ background: '#fff', borderRadius: '32px', padding: '40px', width: '550px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <h3 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 900 }}>Add Student Manually</h3>
                <button onClick={() => setShowManualModal(false)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#64748b' }}>×</button>
              </div>
              <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '32px' }}>Enter student details. This will save to both global database and the active exam.</p>
              
              <form onSubmit={handleAddManualStudent}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginBottom: '32px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: 800, color: '#94a3b8', marginBottom: '6px' }}>STUDENT NAME (ENGLISH) *</label>
                    <input 
                      type="text" 
                      required 
                      className="form-control" 
                      style={{ borderRadius: '12px', padding: '12px' }} 
                      value={manualForm.studentNameEn} 
                      onChange={e => setManualForm({ ...manualForm, studentNameEn: e.target.value })} 
                      placeholder="e.g. TASNIM ALAM"
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: 800, color: '#94a3b8', marginBottom: '6px' }}>FATHER'S NAME (ENGLISH) *</label>
                    <input 
                      type="text" 
                      required 
                      className="form-control" 
                      style={{ borderRadius: '12px', padding: '12px' }} 
                      value={manualForm.fatherNameEn} 
                      onChange={e => setManualForm({ ...manualForm, fatherNameEn: e.target.value })} 
                      placeholder="e.g. NURUL ALAM"
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: 800, color: '#94a3b8', marginBottom: '6px' }}>CLASS *</label>
                      <select 
                        required 
                        className="form-control" 
                        style={{ borderRadius: '12px', padding: '12px' }} 
                        value={manualForm.studentClass} 
                        onChange={e => setManualForm({ ...manualForm, studentClass: e.target.value })}
                      >
                        <option value="">Select...</option>
                        {[6, 7, 8, 9, 10, 11, 12].map(c => <option key={c} value={c}>Class {c}</option>)}
                      </select>
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: 800, color: '#94a3b8', marginBottom: '6px' }}>SECTION *</label>
                      <input 
                        type="text" 
                        required 
                        className="form-control" 
                        style={{ borderRadius: '12px', padding: '12px' }} 
                        value={manualForm.studentSection} 
                        onChange={e => setManualForm({ ...manualForm, studentSection: e.target.value })} 
                        placeholder="e.g. A"
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: 800, color: '#94a3b8', marginBottom: '6px' }}>ROLL NO *</label>
                      <input 
                        type="number" 
                        required 
                        className="form-control" 
                        style={{ borderRadius: '12px', padding: '12px' }} 
                        value={manualForm.studentRoll} 
                        onChange={e => setManualForm({ ...manualForm, studentRoll: e.target.value })} 
                        placeholder="e.g. 15"
                      />
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: 800, color: '#94a3b8', marginBottom: '6px' }}>OPTIONAL / 4TH SUBJECT</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      style={{ borderRadius: '12px', padding: '12px' }} 
                      value={manualForm.optionalSubject} 
                      onChange={e => setManualForm({ ...manualForm, optionalSubject: e.target.value })} 
                      placeholder="e.g. Biology, Higher Mathematics, Agriculture"
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: 800, color: '#94a3b8', marginBottom: '6px' }}>FATHER'S MOBILE (OPTIONAL)</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      style={{ borderRadius: '12px', padding: '12px' }} 
                      value={manualForm.fatherMobile} 
                      onChange={e => setManualForm({ ...manualForm, fatherMobile: e.target.value })} 
                      placeholder="e.g. 01712345678"
                    />
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '12px' }}>
                  <button type="submit" className="btn btn-primary" style={{ flex: 2, borderRadius: '16px', padding: '16px', background: '#4f46e5', border: 'none', fontWeight: 800, color: '#fff', cursor: 'pointer' }}>Add Student</button>
                  <button type="button" className="btn btn-secondary" style={{ flex: 1, borderRadius: '16px', padding: '16px', background: '#f1f5f9', border: 'none', fontWeight: 700, color: '#475569', cursor: 'pointer' }} onClick={() => setShowManualModal(false)}>Cancel</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* CSV IMPORT MODAL */}
        {showCsvModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)' }}>
            <div style={{ background: '#fff', borderRadius: '32px', padding: '40px', width: '750px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <h3 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 900 }}>Upload Students CSV</h3>
                <button onClick={() => { setShowCsvModal(false); setCsvStudents([]); }} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#64748b' }}>×</button>
              </div>
              <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '24px' }}>Import multiple students at once. Columns must include: <b>Name, Father Name, Class, Section, Roll</b> (Optional: <b>Optional Subject, Father Mobile</b>).</p>
              
              {csvStudents.length === 0 ? (
                <div style={{ border: '3px dashed #cbd5e1', borderRadius: '24px', padding: '40px', textAlign: 'center', cursor: 'pointer', transition: 'all 0.2s', position: 'relative' }}
                  onDragOver={e => e.preventDefault()}
                  onDrop={e => {
                    e.preventDefault();
                    const file = e.dataTransfer.files?.[0];
                    if (file) {
                      const reader = new FileReader();
                      reader.onload = (event) => {
                        const text = event.target?.result as string;
                        try {
                          const rows = parseCSVContent(text);
                          if (rows.length === 0) {
                            alert('No students found in CSV or headers could not be matched.');
                            return;
                          }
                          setCsvStudents(rows);
                        } catch (err: any) {
                          alert('Failed to parse CSV file: ' + err.message);
                        }
                      };
                      reader.readAsText(file);
                    }
                  }}
                >
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>📁</div>
                  <div style={{ fontWeight: 800, fontSize: '16px', color: '#1e293b', marginBottom: '8px' }}>Drag & Drop your CSV file here</div>
                  <div style={{ color: '#64748b', fontSize: '13px', marginBottom: '24px' }}>or choose from your computer</div>
                  
                  <input 
                    type="file" 
                    accept=".csv" 
                    onChange={handleCsvFileSelect}
                    style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }}
                  />
                  <button type="button" style={{ padding: '10px 24px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '12px', fontWeight: 700, pointerEvents: 'none' }}>Choose File</button>
                </div>
              ) : (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <span style={{ fontSize: '14px', fontWeight: 800, color: '#10b981' }}>📊 Ready to Import: {csvStudents.length} Students Detected</span>
                    <button onClick={() => setCsvStudents([])} style={{ background: '#fef2f2', border: 'none', color: '#ef4444', fontWeight: 800, padding: '6px 12px', borderRadius: '8px', cursor: 'pointer', fontSize: '11px' }}>RESET FILE</button>
                  </div>
                  
                  <div style={{ maxHeight: '350px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '16px', marginBottom: '32px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', textAlign: 'left' }}>
                      <thead style={{ background: '#f8fafc', position: 'sticky', top: 0, borderBottom: '1px solid #e2e8f0', zIndex: 1 }}>
                        <tr>
                          <th style={{ padding: '12px' }}>Name</th>
                          <th style={{ padding: '12px' }}>Father's Name</th>
                          <th style={{ padding: '12px' }}>Class</th>
                          <th style={{ padding: '12px' }}>Section</th>
                          <th style={{ padding: '12px' }}>Roll</th>
                          <th style={{ padding: '12px' }}>Optional Subject</th>
                          <th style={{ padding: '12px' }}>Mobile</th>
                        </tr>
                      </thead>
                      <tbody>
                        {csvStudents.map((s, idx) => {
                          const isInvalid = !s.studentNameEn || !s.fatherNameEn || !s.studentClass || !s.studentSection || !s.studentRoll;
                          return (
                            <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9', background: isInvalid ? '#fff1f2' : 'none' }}>
                              <td style={{ padding: '12px', fontWeight: 700, color: s.studentNameEn ? '#1e293b' : '#ef4444' }}>{s.studentNameEn || '[MISSING]'}</td>
                              <td style={{ padding: '12px', color: s.fatherNameEn ? '#1e293b' : '#ef4444' }}>{s.fatherNameEn || '[MISSING]'}</td>
                              <td style={{ padding: '12px', color: s.studentClass ? '#1e293b' : '#ef4444' }}>{s.studentClass || '[MISSING]'}</td>
                              <td style={{ padding: '12px', color: s.studentSection ? '#1e293b' : '#ef4444' }}>{s.studentSection || '[MISSING]'}</td>
                              <td style={{ padding: '12px', color: s.studentRoll ? '#1e293b' : '#ef4444' }}>{s.studentRoll || '[MISSING]'}</td>
                              <td style={{ padding: '12px', color: '#64748b' }}>{s.optionalSubject || '-'}</td>
                              <td style={{ padding: '12px', color: '#64748b' }}>{s.fatherMobile || '-'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button 
                      onClick={handleImportCsvStudents} 
                      disabled={isProcessingCsv}
                      style={{ flex: 2, borderRadius: '16px', padding: '16px', background: '#059669', border: 'none', fontWeight: 800, color: '#fff', cursor: 'pointer', boxShadow: '0 4px 10px rgba(5,150,105,0.2)' }}
                    >
                      {isProcessingCsv ? 'Importing...' : `Confirm & Import ${csvStudents.length} Students`}
                    </button>
                    <button 
                      type="button" 
                      disabled={isProcessingCsv}
                      style={{ flex: 1, borderRadius: '16px', padding: '16px', background: '#f1f5f9', border: 'none', fontWeight: 700, color: '#475569', cursor: 'pointer' }} 
                      onClick={() => { setShowCsvModal(false); setCsvStudents([]); }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* UNLOCK FINAL SUBMIT MODAL */}
        {showUnlockModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)' }}>
            <div style={{ background: '#fff', borderRadius: '32px', padding: '40px', width: '600px', maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <h3 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 900 }}>🔓 Unlock Subject Marks Entry</h3>
                <button onClick={() => setShowUnlockModal(false)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#64748b' }}>×</button>
              </div>
              <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '24px' }}>
                Below are the subjects that have been final-submitted by teachers. Unlocking a subject allows teachers to edit and save marks again.
              </p>

              {loadingSubmitted ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}><div className="spinner" /></div>
              ) : submittedAssignments.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 20px', background: '#f8fafc', borderRadius: '24px', border: '1px dashed #e2e8f0', color: '#64748b', fontWeight: 700 }}>
                  No subjects are currently locked/final submitted!
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' }}>
                  {submittedAssignments.map((a: any) => (
                    <div key={a.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', background: '#f8fafc', borderRadius: '20px', border: '1px solid #e2e8f0' }}>
                      <div style={{ minWidth: 0, flex: 1, marginRight: '16px' }}>
                        <div style={{ fontWeight: 900, color: '#0f172a', fontSize: '15px' }}>{a.subject_name} ({a.subject_code})</div>
                        <div style={{ fontSize: '12px', color: '#4f46e5', fontWeight: 800, marginTop: '4px' }}>
                          CLASS {a.class} • SECTION {a.section}
                        </div>
                        <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                          Teacher: {a.teacher_name_en || 'Unknown'} ({a.teacher_email_id})
                        </div>
                      </div>
                      <button 
                        onClick={() => handleUnlockAssignment(a.id, a.subject_name)}
                        style={{ padding: '8px 16px', borderRadius: '10px', background: '#fee2e2', border: 'none', color: '#ef4444', fontWeight: 800, cursor: 'pointer', transition: '0.2s', display: 'flex', alignItems: 'center', gap: '6px' }}
                      >
                        🔓 Unlock
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button 
                  style={{ borderRadius: '16px', padding: '12px 24px', background: '#f1f5f9', border: 'none', fontWeight: 700, color: '#475569', cursor: 'pointer' }} 
                  onClick={() => setShowUnlockModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

